import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import SimpleFileBrowser from './SimpleFileBrowser';

import logger from '../utils/logger.js';
const SvgPathSection = ({ 
  stickerData, 
  setStickerData, 
  listingData, 
  onSave, 
  showFileBrowser = true,
  title = "Scene Builder File",
  suggestedPath = ""
}) => {
  logger.log('SvgPathSection props:', { suggestedPath, stickerDataSvgPath: stickerData?.svgPath });
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(stickerData?.svgPath || '');

  // Update local state when stickerData changes or when suggestedPath is provided
  useEffect(() => {
    if (suggestedPath && !stickerData?.svgPath) {
      // If we have a suggestion and no current path, use the suggestion
      setCurrentPath(suggestedPath);
      if (stickerData) {
        setStickerData({
          ...stickerData,
          svgPath: suggestedPath
        });
      }
    } else {
      setCurrentPath(stickerData?.svgPath || '');
    }
  }, [stickerData?.svgPath, suggestedPath, stickerData]);

  // Sync local state with parent state
  const handlePathChange = (e) => {
    const newPath = e.target.value;
    setCurrentPath(newPath);
    if (stickerData) {
      setStickerData({
        ...stickerData,
        svgPath: newPath
      });
    }
  };

  // Handle file selection from browser
  const handleFileSelect = (filePath) => {
    logger.log('Selected file path:', filePath);
    setCurrentPath(filePath);
    if (stickerData) {
      setStickerData({
        ...stickerData,
        svgPath: filePath
      });
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave();
    }
  };

  return (
    <Accordion defaultExpanded={true}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {suggestedPath && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)', borderRadius: 1 }}>
            {/* Debug info */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Debug: suggestedPath = "{suggestedPath}", stickerData.svgPath = "{stickerData?.svgPath || 'none'}"
            </Typography>
            
            <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
              💡 Suggestion available: {suggestedPath}
            </Typography>
            
            {/* Thumbnail Preview (reflects current selection if available) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                border: '2px solid rgba(76, 175, 80, 0.5)', 
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={currentPath || suggestedPath} 
                  alt="Selected image preview"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain' 
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box sx={{ 
                  display: 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  textAlign: 'center'
                }}>
                  <span>Image</span>
                  <span>Not Found</span>
                </Box>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  The suggestion has been automatically populated in the field above. You can edit it or save it as-is.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  <strong>File path:</strong> {currentPath || suggestedPath}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Path to scene builder file (SVG or PNG)"
            variant="outlined"
            value={currentPath}
            onChange={handlePathChange}
          />
          {showFileBrowser && (
            <Button
              variant="outlined"
              onClick={() => setFileBrowserOpen(true)}
              sx={{ minWidth: '120px' }}
            >
              Browse Files
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ minWidth: '120px' }}
          >
            Save Path
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Current path: {currentPath || 'None selected'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Select an SVG or PNG file. The path will be saved as a relative path to the public/assets/images/ folder.
          Click "Save Path" to save just the scene builder file path to the database.
        </Typography>
        
        {showFileBrowser && (
          <SimpleFileBrowser
            open={fileBrowserOpen}
            onClose={() => setFileBrowserOpen(false)}
            onFileSelect={handleFileSelect}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

SvgPathSection.propTypes = {
  stickerData: PropTypes.object,
  setStickerData: PropTypes.func.isRequired,
  listingData: PropTypes.object,
  onSave: PropTypes.func,
  showFileBrowser: PropTypes.bool,
  title: PropTypes.string,
  suggestedPath: PropTypes.string
};

export default SvgPathSection;
